import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Textarea } from "@/components/ui/textarea/textarea";
import { createNewAllowanceRequest } from "@/config/mutation/employee";
import { AxiosError } from "axios";
import { UploadAttachment } from "./upload-attachement";

const formSchema = z.object({
  reason: z
    .string({
      required_error: "Reason is Required!",
    })
    .min(1, {
      message: "Reason is Required!",
    }),
  amountRequested: z.coerce.number().min(0).default(0),
  attachment: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export const NewAllowanceForm = () => {
  const empKey = Cookies.get("key") as string;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: "",
      amountRequested: 0,
      attachment: "",
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createNewAllowanceRequest(empKey),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    // console.log(values);

    mutate(values, {
      onSuccess: async () => {
        // console.log("New Employee", data);
        await queryClient.invalidateQueries({
          queryKey: ["allowances"],
        });

        toast.success("New Allowance Request Created!");

        navigate("/employee/allowance", {
          replace: true,
        });
      },

      onError: (error) => {
        console.log("ERROR WHILE CREATING NEW ALLOWANCE", error);
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });
  }

  return (
    <div className="h-full rounded-md relative">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex items-center justify-between sticky top-0 px-4 z-30 rounded-md bg-background">
            <h3 className="text-2xl font-medium">New Allowance</h3>

            <Button type="submit" size="sm" disabled={isPending}>
              Save
            </Button>
          </div>

          <div className="py-5 px-5 mt-5 rounded-b-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <FormField
                control={form.control}
                name="amountRequested"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount Requested</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        {...field}
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason</FormLabel>
                    <FormControl>
                      <Textarea rows={5} {...field} disabled={isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <FormField
                  control={form.control}
                  name="attachment"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Upload Attachment</FormLabel>
                      <FormControl>
                        <UploadAttachment
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};
